import { useAuthContext } from '@/auth/useAuthContext';
import {
  type TaskBoardBookingRow,
  type TaskBoardColumn,
  listTaskBoardColumnBookings,
  listTaskBoardColumns,
} from '@/lib/api';
import { useRealtime } from '@/lib/useRealtime';
import { Plus, Settings2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Sub-spec 06 Plan 4 — tenant-driven task board.
 *
 * Reads `account_task_board_columns` from `/api/task-board/columns`. For each
 * column, fetches matching bookings via the per-column endpoint (server-side
 * filter DSL evaluation). Realtime BOOKING_UPDATED events trigger a refresh.
 *
 * Replaces the Phase 2 hardcoded "bath/nails/groom" service-task layout —
 * task-by-line-item rendering moved elsewhere; this page is now the
 * tenant-configurable booking column board.
 */
export function TaskBoard() {
  const { state } = useAuthContext();
  const { me } = state;
  const facilityId = me?.activeFacility?.id ?? null;

  const [columns, setColumns] = useState<TaskBoardColumn[]>([]);
  const [bookingsByColumn, setBookingsByColumn] = useState<Record<string, TaskBoardBookingRow[]>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cols = await listTaskBoardColumns({ activeOnly: true });
      setColumns(cols);
      const fetched = await Promise.all(
        cols.map(async (col) => ({
          id: col.id,
          rows: await listTaskBoardColumnBookings(col.id, {
            // Pass the active facility timezone so date-range filters resolve
            // correctly; also lets the server-side DSL scope to this facility.
            ...(me?.activeFacility?.timezone
              ? { timezone: me.activeFacility.timezone }
              : me?.activeAccount?.defaultTimezone
                ? { timezone: me.activeAccount.defaultTimezone }
                : {}),
            ...(facilityId ? { facilityId } : {}),
          }),
        })),
      );
      const next: Record<string, TaskBoardBookingRow[]> = {};
      for (const f of fetched) next[f.id] = f.rows;
      setBookingsByColumn(next);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load task board');
    } finally {
      setLoading(false);
    }
  }, [facilityId, me]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  useRealtime(
    useCallback(
      (event) => {
        if (event.type === 'BOOKING_UPDATED' || event.type === 'BOOKING_CREATED') {
          void fetchAll();
        }
      },
      [fetchAll],
    ),
  );

  if (!me) {
    return (
      <div className="p-8 text-muted-foreground">
        {state.loading ? 'Loading…' : 'Not signed in.'}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Task board</h1>
          {facilityId && (
            <p className="mt-1 text-xs text-muted-foreground">Showing the active facility.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/settings/task-board"
            className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-muted-foreground hover:text-foreground press"
          >
            <Settings2 className="size-4" strokeWidth={2} />
            Configure
          </Link>
          <Link
            to="/bookings/new"
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 press"
          >
            <Plus className="size-4" strokeWidth={2} />
            New booking
          </Link>
        </div>
      </div>

      {loading && (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading task board…</div>
      )}

      {!loading && error && (
        <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {!loading && !error && columns.length === 0 && (
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <p className="text-sm text-muted-foreground">
            No task board columns configured.{' '}
            <Link to="/settings/task-board" className="text-primary hover:underline">
              Add one
            </Link>
            .
          </p>
        </div>
      )}

      {!loading && !error && columns.length > 0 && (
        <div className="grid auto-cols-[minmax(16rem,1fr)] grid-flow-col gap-3 overflow-x-auto pb-3">
          {columns.map((col) => {
            const rows = bookingsByColumn[col.id] ?? [];
            return (
              <section
                key={col.id}
                className="flex flex-col rounded-xl border border-border bg-surface"
              >
                <header className="flex items-center justify-between border-b border-border px-3 py-2">
                  <h2 className="text-sm font-medium">{col.name}</h2>
                  <span className="num text-xs text-muted-foreground tabular-nums">
                    {rows.length}
                  </span>
                </header>
                <div className="flex-1 space-y-2 p-2">
                  {rows.length === 0 && (
                    <p className="px-2 py-3 text-xs text-muted-foreground">No bookings.</p>
                  )}
                  {rows.map((row) => {
                    const name =
                      row.customer != null
                        ? `${row.customer.firstName} ${row.customer.lastName}`
                        : 'Customer';
                    const petName = row.pet?.name ?? 'Pet';
                    const path =
                      row.booking.customerId !== '' ? `/customers/${row.booking.customerId}` : '#';
                    return (
                      <Link
                        key={row.booking.id}
                        to={path}
                        className="block rounded-md border border-border/60 bg-background px-3 py-2 text-sm hover:bg-secondary"
                      >
                        <div className="font-medium">{petName}</div>
                        <div className="text-xs text-muted-foreground">{name}</div>
                        {row.stay && (
                          <div className="num mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                            {row.stay.checkIn.slice(0, 10)} → {row.stay.checkout.slice(0, 10)}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
